import { Box, Grid, Text, Button, Loader, Flex } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

import Form from 'components/form';
import AuthContext from 'context/AuthContext';
import useFormHandler from 'hooks/useFormHandler';
import { createEmailSchema } from 'utils/schemaBuilder';
import { useNotification } from 'context/NotificationContext';
import { z } from 'zod';

import classes from './UserProfileForm.module.scss';

const UserProfileForm = () => {
  const {
    data: { account },
    actions: { updateAccount, forgotPassword, checkAvailableEmail },
  } = useContext(AuthContext);

  const { showSuccess, showError } = useNotification();

  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [debouncedEmail] = useDebouncedValue(emailValue, 500);
  const [resetLoading, setResetLoading] = useState(false);

  const initialValues = {
    email: account?.username || '',
  };

  const handleResetPassword = async () => {
    try {
      setResetLoading(true);
      await forgotPassword(account?.username || '');
      showSuccess(`Password reset link sent to ${account?.username}`);
    } catch (error) {
      showError('Failed to send reset password link');
    } finally {
      setResetLoading(false);
    }
  };

  const formSchema = z.object({
    email: createEmailSchema(true), // Email is required for profile
  });

  const {
    form,
    error,
    isValid,
    setError,
    loading: updateEmailLoading,
  } = useFormHandler(formSchema, {
    initialValues,
  });

  useEffect(() => {
    const validateEmail = async () => {
      if (!debouncedEmail || debouncedEmail.trim() === account?.username) {
        setEmailCheckLoading(false);
        return;
      }

      const errorMessage = 'Email is already in use';

      try {
        const isAvailable = await checkAvailableEmail(debouncedEmail);

        if (!isAvailable) {
          form.setErrors({
            email: errorMessage,
          });
        }
      } catch (error: any) {
        if (error.status === 400) {
          form.setErrors({
            email: errorMessage,
          });
        }
      } finally {
        setEmailCheckLoading(false);
      }
    };

    validateEmail();
    // Intentionally excluding validateEmail function from dependencies as it should only
    // trigger when the debounced email value changes, not when the function reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail]);

  const onUpdateEmail = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateAccount(values.email);
      showSuccess('Email updated successfully');
    } catch (error) {
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <Box px="lg">
      <Grid gutter={20} mb={{ base: 20, lg: 30 }}>
        <Grid.Col span={{ lg: 8, base: 12 }}>
          <Text className={classes.title} mb={2} fw={600}>
            Personal Information
          </Text>
          <Text mb={2} size="sm" c="dimmed">
            Update your personal details
          </Text>
        </Grid.Col>

        <Grid.Col span={{ lg: 8, base: 12 }}>
          <Form form={form} error={error} onSubmit={onUpdateEmail}>
            <Flex direction="column" gap={0} mb={10}>
              <Text size="sm" fw={500} mb={7}>
                Email
              </Text>
              <Flex
                gap="md"
                justify="space-between"
                direction={{ base: 'column', sm: 'row' }}
              >
                <Form.Input
                  m={0}
                  flex={1}
                  size="md"
                  name="email"
                  type="email"
                  loading={emailCheckLoading}
                  placeholder="Ex: michaelscott@dundermifflin.com"
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  description="This is the email address you will used to login and receive product and billing notifications."
                  onChange={(value: string | number) => {
                    if (value !== account?.username) {
                      setEmailCheckLoading(true);
                    }
                    setEmailValue(value as string);
                  }}
                  onBlur={() => {
                    // do nothing
                  }}
                />
                <Form.Submit
                  mt={0}
                  size="md"
                  h="2.75rem"
                  w={{ base: '100%', sm: 'fit-content' }}
                  variant="default"
                  loading={updateEmailLoading}
                  disabled={
                    !isValid ||
                    updateEmailLoading ||
                    emailCheckLoading ||
                    account?.username === form.values.email
                  }
                >
                  Update email
                </Form.Submit>
              </Flex>
            </Flex>
          </Form>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ lg: 8, base: 12 }} mb={20}>
          <Text className={classes.title} mb={2} fw={600}>
            Security Settings
          </Text>
          <Text mb={2} size="sm" c="dimmed">
            Manage your account security
          </Text>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={{ lg: 4, base: 12 }}>
          <Text size="sm" mb="md" fw={500}>
            Reset your password
          </Text>
          <Button
            fullWidth
            onClick={handleResetPassword}
            rightSection={
              resetLoading ? <Loader size="14" color="#adb5bd" /> : null
            }
            disabled={resetLoading}
          >
            Send password reset link
          </Button>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default UserProfileForm;
